export interface StibeeInputFile {
  name: string;
  relativePath: string;
  text: string;
  size: number;
}

const EXPECTED_CSV = [
  '발송 성공',
  '발송 실패',
  '오픈',
  '오픈 안 함',
  '클릭',
  '클릭 안 함',
  '링크별 클릭',
  '수신거부',
] as const;

const EMAIL_RE = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i;

const looksLikeEmail = (value: string) => EMAIL_RE.test(value);

const hideEmails = (value: string) =>
  value.replace(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, '(메일주소)');

const formatNum = (n: number) => Number(n || 0).toLocaleString('ko-KR');

const formatPct = (part: number, whole: number) => {
  if (!whole) return '-';
  return `${((part / whole) * 100).toFixed(1)}%`;
};

const baseName = (name: string) => name.replace(/\.[^.]+$/, '').trim();

const csvKind = (name: string) => {
  const key = baseName(name);
  const found = [...EXPECTED_CSV].sort((a, b) => b.length - a.length).find((label) => key === label);
  return found || '';
};

export const folderNameOf = (files: StibeeInputFile[]) => {
  for (const file of files) {
    const path = (file.relativePath || file.name).replace(/\\/g, '/');
    const parts = path.split('/').filter(Boolean);
    if (parts.length > 1) return parts[0];
  }
  return '선택한 파일';
};

const parseCsv = (text: string): string[][] => {
  const src = text.replace(/^\uFEFF/, '');
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let quoted = false;

  for (let i = 0; i < src.length; i += 1) {
    const ch = src[i];
    if (quoted) {
      if (ch === '"') {
        if (src[i + 1] === '"') {
          cell += '"';
          i += 1;
        } else {
          quoted = false;
        }
      } else {
        cell += ch;
      }
      continue;
    }
    if (ch === '"') {
      quoted = true;
      continue;
    }
    if (ch === ',') {
      row.push(cell);
      cell = '';
      continue;
    }
    if (ch === '\n') {
      row.push(cell);
      rows.push(row);
      row = [];
      cell = '';
      continue;
    }
    if (ch === '\r') continue;
    cell += ch;
  }

  if (cell.length || row.length) {
    row.push(cell);
    rows.push(row);
  }

  return rows.filter((item) => item.some((value) => value.trim() !== ''));
};

const colIndex = (header: string[], name: string) => header.findIndex((item) => item.trim() === name);

const dataRows = (rows: string[][]) => (rows.length > 1 ? rows.slice(1) : []);

const countColumn = (rows: string[][], columnName: string) => {
  if (!rows.length) return [] as Array<[string, number]>;
  const header = rows[0].map((item) => item.trim());
  const index = colIndex(header, columnName);
  if (index < 0) return [];
  const map = new Map<string, number>();
  dataRows(rows).forEach((row) => {
    const raw = (row[index] || '').trim();
    if (!raw || looksLikeEmail(raw)) return;
    map.set(raw, (map.get(raw) || 0) + 1);
  });
  return [...map.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0], 'ko'));
};

const sumColumn = (rows: string[][], columnName: string) => {
  if (!rows.length) return 0;
  const header = rows[0].map((item) => item.trim());
  const index = colIndex(header, columnName);
  if (index < 0) return 0;
  return dataRows(rows).reduce((sum, row) => sum + (Number(row[index]) || 0), 0);
};

const dateRange = (rows: string[][], columnName: string) => {
  if (!rows.length) return '';
  const header = rows[0].map((item) => item.trim());
  const index = colIndex(header, columnName);
  if (index < 0) return '';
  const values = dataRows(rows)
    .map((row) => (row[index] || '').trim())
    .filter((value) => value && !looksLikeEmail(value))
    .sort();
  if (!values.length) return '';
  if (values[0] === values[values.length - 1]) return values[0];
  return `${values[0]} ~ ${values[values.length - 1]}`;
};

const pushCounts = (lines: string[], title: string, pairs: Array<[string, number]>) => {
  lines.push('');
  lines.push(title);
  if (!pairs.length) {
    lines.push('없음');
    return;
  }
  pairs.forEach(([label, count]) => {
    lines.push(`${hideEmails(label)} ${formatNum(count)}건`);
  });
};

export const analyzeStibeeFiles = (files: StibeeInputFile[]) => {
  const csv = new Map<string, { name: string; rows: string[][] }>();
  const unknown: string[] = [];

  files.forEach((file) => {
    const lower = file.name.toLowerCase();
    if (lower.endsWith('.html') || lower.endsWith('.htm')) return;
    if (!lower.endsWith('.csv')) {
      unknown.push(file.name);
      return;
    }
    const kind = csvKind(file.name);
    if (!kind) {
      unknown.push(file.name);
      return;
    }
    csv.set(kind, { name: file.name, rows: parseCsv(file.text) });
  });

  const countOf = (kind: string) => {
    const item = csv.get(kind);
    if (!item) return 0;
    return Math.max(0, item.rows.length - (item.rows.length ? 1 : 0));
  };

  const rowsOf = (kind: string) => csv.get(kind)?.rows || [];
  const sentOk = countOf('발송 성공');
  const opened = countOf('오픈');
  const clicked = countOf('클릭');

  const lines: string[] = [];
  lines.push(`폴더`);
  lines.push(folderNameOf(files));
  lines.push('');
  lines.push('메일 주소는 세지 않았습니다. 건수만 모았습니다. 이 내용은 저장되지 않습니다.');

  lines.push('');
  lines.push('건수');
  lines.push(`발송 성공 ${formatNum(sentOk)}건`);
  lines.push(`발송 실패 ${formatNum(countOf('발송 실패'))}건`);
  lines.push(`오픈 ${formatNum(opened)}건`);
  lines.push(`오픈 안 함 ${formatNum(countOf('오픈 안 함'))}건`);
  lines.push(`클릭 ${formatNum(clicked)}건`);
  lines.push(`클릭 안 함 ${formatNum(countOf('클릭 안 함'))}건`);
  lines.push(`수신거부 ${formatNum(countOf('수신거부'))}건`);

  lines.push('');
  lines.push('비율 (발송 성공 기준)');
  lines.push(`오픈율 ${formatPct(opened, sentOk)}`);
  lines.push(`클릭율 ${formatPct(clicked, sentOk)}`);
  lines.push(`오픈 대비 클릭 ${formatPct(clicked, opened)}`);

  lines.push('');
  lines.push('중복 포함 합계');
  lines.push(`오픈(중복 포함) ${formatNum(sumColumn(rowsOf('오픈'), '오픈(중복 포함)'))}`);
  lines.push(`클릭(중복 포함) ${formatNum(sumColumn(rowsOf('클릭'), '클릭(중복 포함)'))}`);

  const sentRange = dateRange(rowsOf('발송 성공'), '발송완료일');
  const openRange = dateRange(rowsOf('오픈'), '오픈일');
  const clickRange = dateRange(rowsOf('클릭'), '클릭일');
  const unsubRange = dateRange(rowsOf('수신거부'), '수신거부일');
  lines.push('');
  lines.push('기간');
  lines.push(`발송완료일 ${sentRange || '-'}`);
  lines.push(`오픈일 ${openRange || '-'}`);
  lines.push(`클릭일 ${clickRange || '-'}`);
  lines.push(`수신거부일 ${unsubRange || '-'}`);

  pushCounts(lines, '발송 실패 이유', countColumn(rowsOf('발송 실패'), '발송 실패 이유'));
  pushCounts(lines, '이메일 수신 상태 (발송 성공)', countColumn(rowsOf('발송 성공'), '이메일 수신 상태'));
  pushCounts(lines, '이메일 수신 상태 (오픈)', countColumn(rowsOf('오픈'), '이메일 수신 상태'));
  pushCounts(lines, '이메일 수신 상태 (클릭)', countColumn(rowsOf('클릭'), '이메일 수신 상태'));
  pushCounts(lines, '이메일 수신 상태 (수신거부)', countColumn(rowsOf('수신거부'), '이메일 수신 상태'));

  lines.push('');
  lines.push('링크별 클릭');
  const linkRows = rowsOf('링크별 클릭');
  const linkHeader = (linkRows[0] || []).map((item) => item.trim());
  const linkIdx = colIndex(linkHeader, '링크');
  const clickIdx = colIndex(linkHeader, '클릭');
  const dupIdx = colIndex(linkHeader, '클릭(중복포함)');
  const linkData = dataRows(linkRows);
  if (!linkData.length || linkIdx < 0) {
    lines.push('없음');
  } else {
    linkData.forEach((row) => {
      const link = hideEmails((row[linkIdx] || '').trim() || '(링크 없음)');
      const clicks = formatNum(Number(row[clickIdx]) || 0);
      const dups = formatNum(Number(row[dupIdx]) || 0);
      lines.push(`${link} · 클릭 ${clicks} · 중복포함 ${dups}`);
    });
  }

  lines.push('');
  lines.push('읽은 파일');
  EXPECTED_CSV.forEach((kind) => {
    const item = csv.get(kind);
    if (!item) {
      lines.push(`${kind}.csv 없음`);
      return;
    }
    lines.push(`${item.name} ${formatNum(countOf(kind))}행`);
  });
  if (unknown.length) {
    lines.push('');
    lines.push('제외한 파일');
    unknown.forEach((name) => lines.push(name));
  }

  return lines.join('\n');
};

export interface StibeeEmailGroups {
  sentOk: string[];
  sentFail: string[];
  opened: string[];
  clicked: string[];
  unsub: string[];
}

const EMAIL_GROUP: Record<string, keyof StibeeEmailGroups> = {
  '발송 성공': 'sentOk',
  '발송 실패': 'sentFail',
  오픈: 'opened',
  클릭: 'clicked',
  수신거부: 'unsub',
};

export const collectStibeeEmailGroups = (files: StibeeInputFile[]): StibeeEmailGroups => {
  const groups: StibeeEmailGroups = {
    sentOk: [],
    sentFail: [],
    opened: [],
    clicked: [],
    unsub: [],
  };

  files.forEach((file) => {
    if (!file.name.toLowerCase().endsWith('.csv')) return;
    const key = EMAIL_GROUP[csvKind(file.name)];
    if (!key) return;
    const rows = parseCsv(file.text);
    if (!rows.length) return;
    const header = rows[0].map((item) => item.trim());
    const index = colIndex(header, '이메일 주소');
    if (index < 0) return;
    const seen = new Set<string>();
    dataRows(rows).forEach((row) => {
      const email = (row[index] || '').trim().toLowerCase();
      if (!email.includes('@') || seen.has(email)) return;
      seen.add(email);
      groups[key].push(email);
    });
  });

  return groups;
};

export interface RegionCounts {
  name: string;
  sentOk: number;
  sentFail: number;
  opened: number;
  clicked: number;
  unsub: number;
}

export interface RegionMatchResult {
  provinces: RegionCounts[];
  unmatched: RegionCounts;
  multiRegionSentOk: number;
}

const regionLine = (item: RegionCounts) => {
  const openRate = formatPct(item.opened, item.sentOk);
  const clickRate = formatPct(item.clicked, item.sentOk);
  return [
    `${item.name}`,
    `발송 성공 ${formatNum(item.sentOk)}건 · 오픈 ${formatNum(item.opened)}건 (${openRate}) · 클릭 ${formatNum(item.clicked)}건 (${clickRate})`,
    `발송 실패 ${formatNum(item.sentFail)}건 · 수신거부 ${formatNum(item.unsub)}건`,
  ];
};

export const formatRegionText = (data: RegionMatchResult) => {
  const lines = [
    '지역',
    '크롤한 장기요양기관 메일과 같은 주소만 지역으로 나눴습니다.',
    '서울·인천은 경기, 대전·세종은 충남, 광주는 전남, 대구는 경북, 부산·울산은 경남입니다. 제주는 따로 둡니다.',
    '한 메일이 여러 지역 기관에 있으면 그 지역마다 1건입니다.',
  ];
  if (data.multiRegionSentOk > 0) {
    lines.push(`지역이 둘 이상인 발송 성공 메일 ${formatNum(data.multiRegionSentOk)}건`);
  }
  [...data.provinces, data.unmatched].forEach((item) => {
    lines.push('');
    regionLine(item).forEach((line) => lines.push(line));
  });
  return lines.join('\n');
};

export interface StibeeTimedEvent {
  email: string;
  at: string;
}

export interface StibeeCampaignPayload {
  name: string;
  sent: StibeeTimedEvent[];
  opens: StibeeTimedEvent[];
  clicks: StibeeTimedEvent[];
  mailProducts: string[];
}

const timedEvents = (files: StibeeInputFile[], kind: string, dateColumn: string) => {
  const file = files.find((item) => item.name.toLowerCase().endsWith('.csv') && csvKind(item.name) === kind);
  if (!file) return [] as StibeeTimedEvent[];
  const rows = parseCsv(file.text);
  if (!rows.length) return [];
  const header = rows[0].map((item) => item.trim());
  const emailIndex = colIndex(header, '이메일 주소');
  const dateIndex = colIndex(header, dateColumn);
  if (emailIndex < 0 || dateIndex < 0) return [];
  const earliest = new Map<string, string>();
  dataRows(rows).forEach((row) => {
    const email = (row[emailIndex] || '').trim().toLowerCase();
    const at = (row[dateIndex] || '').trim();
    if (!email.includes('@') || !/^\d{4}-\d{2}-\d{2}/.test(at)) return;
    const prev = earliest.get(email);
    if (!prev || at < prev) earliest.set(email, at);
  });
  return [...earliest.entries()].map(([email, at]) => ({ email, at }));
};

export const mailProductNames = (html: string) => {
  const found: string[] = [];
  const push = (raw: string) => {
    const text = raw.replace(/\s+/g, ' ').trim();
    if (text.length < 4 || text.length > 40) return;
    if (/[,.，]|[?？→]|까요|합니다|습니다|세요|입니다|마세요/.test(text)) return;
    if (!/[가-힣]/.test(text)) return;
    if (text.split(' ').length > 8) return;
    if (!found.includes(text)) found.push(text);
  };
  const alts = html.match(/alt="([^"]+)"/gi) || [];
  alts.forEach((chunk) => {
    const matched = chunk.match(/alt="([^"]+)"/i);
    if (matched?.[1]) push(matched[1]);
  });
  const titles = html.match(/font-weight:\s*[679]00[^>]*>([^<]+)</gi) || [];
  titles.forEach((chunk) => {
    const matched = chunk.match(/>([^<]+)</);
    if (matched?.[1]) push(matched[1]);
  });
  return found.slice(0, 30);
};

export interface StibeeStoredEvent {
  email: string;
  at: string;
  status: string;
  dup: number;
  reason: string;
}

const storedEvents = (
  files: StibeeInputFile[],
  kind: string,
  dateColumn: string,
  dupColumn = ''
) => {
  const file = files.find((item) => item.name.toLowerCase().endsWith('.csv') && csvKind(item.name) === kind);
  if (!file) return [] as StibeeStoredEvent[];
  const rows = parseCsv(file.text);
  if (!rows.length) return [];
  const header = rows[0].map((item) => item.trim());
  const emailIndex = colIndex(header, '이메일 주소');
  const dateIndex = colIndex(header, dateColumn);
  const statusIndex = colIndex(header, '이메일 수신 상태');
  const reasonIndex = colIndex(header, '발송 실패 이유');
  const dupIndex = dupColumn ? colIndex(header, dupColumn) : -1;
  if (emailIndex < 0 || dateIndex < 0) return [];
  const earliest = new Map<string, StibeeStoredEvent>();
  dataRows(rows).forEach((row) => {
    const email = (row[emailIndex] || '').trim().toLowerCase();
    const at = (row[dateIndex] || '').trim();
    if (!email.includes('@') || !/^\d{4}-\d{2}-\d{2}/.test(at)) return;
    const prev = earliest.get(email);
    if (prev && prev.at <= at) return;
    earliest.set(email, {
      email,
      at,
      status: statusIndex >= 0 ? (row[statusIndex] || '').trim() : '',
      dup: dupIndex >= 0 ? Number(row[dupIndex]) || 0 : 0,
      reason: reasonIndex >= 0 ? (row[reasonIndex] || '').trim() : '',
    });
  });
  return [...earliest.values()];
};

const collectLinks = (files: StibeeInputFile[]) => {
  const file = files.find((item) => item.name.toLowerCase().endsWith('.csv') && csvKind(item.name) === '링크별 클릭');
  if (!file) return [] as Array<{ url: string; clicks: number; dup: number }>;
  const rows = parseCsv(file.text);
  if (!rows.length) return [];
  const header = rows[0].map((item) => item.trim());
  const linkIndex = colIndex(header, '링크');
  const clickIndex = colIndex(header, '클릭');
  const dupIndex = colIndex(header, '클릭(중복포함)');
  if (linkIndex < 0) return [];
  return dataRows(rows).map((row) => ({
    url: (row[linkIndex] || '').trim(),
    clicks: Number(row[clickIndex]) || 0,
    dup: Number(row[dupIndex]) || 0,
  })).filter((item) => item.url && !item.url.includes('@'));
};

export const collectStibeeSavePayload = (files: StibeeInputFile[], name: string) => {
  const htmlFiles = files.filter((file) => /\.html?$/i.test(file.name));
  const htmlFile = htmlFiles.sort((a, b) => b.size - a.size)[0];
  return {
    name,
    htmlName: htmlFile?.name || '',
    html: htmlFile?.text || '',
    mailProducts: mailProductNames(htmlFile?.text || ''),
    links: collectLinks(files),
    sent: storedEvents(files, '발송 성공', '발송완료일'),
    opens: storedEvents(files, '오픈', '오픈일', '오픈(중복 포함)'),
    clicks: storedEvents(files, '클릭', '클릭일', '클릭(중복 포함)'),
    fails: storedEvents(files, '발송 실패', '발송완료일'),
    unsubs: storedEvents(files, '수신거부', '수신거부일'),
  };
};

export const collectStibeeCampaign = (files: StibeeInputFile[], name: string): StibeeCampaignPayload => {
  const html = files
    .filter((file) => /\.html?$/i.test(file.name))
    .map((file) => file.text)
    .join('\n');
  return {
    name,
    sent: timedEvents(files, '발송 성공', '발송완료일'),
    opens: timedEvents(files, '오픈', '오픈일'),
    clicks: timedEvents(files, '클릭', '클릭일'),
    mailProducts: mailProductNames(html),
  };
};

interface MoneyRow {
  buyers: number;
  orders: number;
  amount: number;
}

interface ProductRow {
  name: string;
  qty: number;
  orders: number;
  amount: number;
}

interface RegionSaleRow {
  name: string;
  orders: number;
  amount: number;
}

export interface CampaignEffectRow {
  name: string;
  clicked: MoneyRow;
  openOnly: MoneyRow;
  notOpen: MoneyRow;
  mailProducts: ProductRow[];
  otherProducts: ProductRow[];
  provinces: RegionSaleRow[];
  unknownRegion: RegionSaleRow;
  cartOnly: number;
}

export interface InstitutionSalesRow {
  name: string;
  orders: number;
  sales: number;
  commission: number;
  settled: number;
  pending: number;
  viaClick: boolean;
}

export interface InstitutionSalesTotals {
  count: number;
  sales: number;
  commission: number;
  settled: number;
  pending: number;
}

export interface EffectMatchResult {
  campaigns: CampaignEffectRow[];
  lastClick: Array<MoneyRow & { name: string }>;
  boughtWithoutClick: MoneyRow;
  afterSend?: Array<MoneyRow & { name: string }>;
  afterSendRegions?: Array<{ name: string; orders: number; amount: number }>;
  afterSendProducts?: Array<ProductRow & { isMail: boolean }>;
  afterSendShipping?: number;
  afterSendTimeline?: Array<{
    name: string;
    sentAt: string;
    orders: number;
    amount: number;
    days: Array<{ day: string; orders: number; amount: number; note: string }>;
  }>;
  institutions?: InstitutionSalesRow[];
  institutionTotals?: InstitutionSalesTotals | null;
}

const won = (amount: number) => `${formatNum(amount)}원`;

const moneyLine = (label: string, row: MoneyRow) =>
  `${label} 구매 기관 ${formatNum(row.buyers)}곳 · 주문 ${formatNum(row.orders)}건 · ${won(row.amount)}`;

const productBlock = (title: string, rows: ProductRow[], lines: string[]) => {
  lines.push('');
  lines.push(title);
  if (!rows.length) {
    lines.push('없음');
    return;
  }
  rows.forEach((row) => {
    lines.push(
      `${row.name} · ${formatNum(row.qty)}개 · 주문 ${formatNum(row.orders)}건 · ${won(row.amount)}`
    );
  });
};

export interface StoredCampaignStats {
  sentOk: number;
  sentFail: number;
  opened: number;
  notOpen: number;
  clicked: number;
  notClick: number;
  unsub: number;
  openDup: number;
  clickDup: number;
  sentRange: { from: string; to: string };
  openRange: { from: string; to: string };
  clickRange: { from: string; to: string };
  unsubRange: { from: string; to: string };
  failReasons: Array<{ name: string; count: number }>;
  sentStatus: Array<{ name: string; count: number }>;
  openStatus: Array<{ name: string; count: number }>;
  clickStatus: Array<{ name: string; count: number }>;
  unsubStatus: Array<{ name: string; count: number }>;
  links: Array<{ url: string; clicks: number; dup: number }>;
}

const showRange = (range?: { from: string; to: string }) => {
  if (!range?.from) return '-';
  if (!range.to || range.from === range.to) return range.from;
  return `${range.from} ~ ${range.to}`;
};

const pushNamedCounts = (
  lines: string[],
  title: string,
  pairs: Array<{ name: string; count: number }> | undefined
) => {
  lines.push('');
  lines.push(title);
  if (!pairs?.length) {
    lines.push('없음');
    return;
  }
  pairs.forEach((item) => lines.push(`${hideEmails(item.name)} ${formatNum(item.count)}건`));
};

export const formatStoredStatsText = (stats: StoredCampaignStats) => {
  const lines = [
    '건수',
    `발송 성공 ${formatNum(stats.sentOk)}건`,
    `발송 실패 ${formatNum(stats.sentFail)}건`,
    `오픈 ${formatNum(stats.opened)}건`,
    `오픈 안 함 ${formatNum(stats.notOpen)}건`,
    `클릭 ${formatNum(stats.clicked)}건`,
    `클릭 안 함 ${formatNum(stats.notClick)}건`,
    `수신거부 ${formatNum(stats.unsub)}건`,
    '',
    '비율 (발송 성공 기준)',
    `오픈율 ${formatPct(stats.opened, stats.sentOk)}`,
    `클릭율 ${formatPct(stats.clicked, stats.sentOk)}`,
    `오픈 대비 클릭 ${formatPct(stats.clicked, stats.opened)}`,
    '',
    '중복 포함 합계',
    `오픈(중복 포함) ${formatNum(stats.openDup)}`,
    `클릭(중복 포함) ${formatNum(stats.clickDup)}`,
    '',
    '기간',
    `발송완료일 ${showRange(stats.sentRange)}`,
    `오픈일 ${showRange(stats.openRange)}`,
    `클릭일 ${showRange(stats.clickRange)}`,
    `수신거부일 ${showRange(stats.unsubRange)}`,
  ];
  pushNamedCounts(lines, '발송 실패 이유', stats.failReasons);
  pushNamedCounts(lines, '이메일 수신 상태 (발송 성공)', stats.sentStatus);
  pushNamedCounts(lines, '이메일 수신 상태 (오픈)', stats.openStatus);
  pushNamedCounts(lines, '이메일 수신 상태 (클릭)', stats.clickStatus);
  pushNamedCounts(lines, '이메일 수신 상태 (수신거부)', stats.unsubStatus);
  lines.push('');
  lines.push('링크별 클릭');
  if (!stats.links?.length) lines.push('없음');
  else {
    stats.links.forEach((link) => {
      lines.push(
        `${hideEmails(link.url)} · 클릭 ${formatNum(link.clicks)} · 중복포함 ${formatNum(link.dup)}`
      );
    });
  }
  return lines.join('\n');
};

export const formatEffectText = (row: CampaignEffectRow) => {
  const lines = [
    '구매',
    '결제된 주문만 셉니다. 클릭·열람·발송 시각 이후에 같은 기관이 산 주문입니다.',
    '주문에 유입 경로가 없어, 메일을 본 뒤 산 것을 겹친 건수로 둡니다.',
    '',
    moneyLine('클릭한 곳', row.clicked),
    moneyLine('열기만 한 곳', row.openOnly),
    moneyLine('열지 않은 곳', row.notOpen),
  ];
  productBlock('메일에 있던 상품 (클릭 이후 구매)', row.mailProducts, lines);
  productBlock('그 외 상품 (클릭 이후 구매)', row.otherProducts, lines);
  lines.push('');
  lines.push('지역별 구매 (클릭 이후)');
  const regions = row.provinces.filter((item) => item.orders > 0);
  if (!regions.length && row.unknownRegion.orders < 1) {
    lines.push('없음');
  } else {
    regions.forEach((item) => {
      lines.push(`${item.name} 주문 ${formatNum(item.orders)}건 · ${won(item.amount)}`);
    });
    if (row.unknownRegion.orders > 0) {
      lines.push(`지역 미상 주문 ${formatNum(row.unknownRegion.orders)}건 · ${won(row.unknownRegion.amount)}`);
    }
  }
  lines.push('');
  lines.push(`장바구니만 ${formatNum(row.cartOnly)}곳`);
  lines.push('클릭 이후 장바구니에 담고, 결제는 없는 기관입니다.');
  return lines.join('\n');
};

export const promoMailLabel = (folderName: string) => {
  const name = String(folderName || '');
  const matched = name.match(/_전체 통계_(\d{14})$/) || name.match(/_(\d{14})$/);
  if (!matched) return name;
  const title = name
    .slice(0, name.length - matched[0].length)
    .replace(/_+$/g, '')
    .trim();
  return `${title} ${matched[1]}`;
};

export const formatLastClickText = (data: EffectMatchResult) => {
  const lines = [
    '여러 메일 중 주문 직전 클릭',
    '한 기관이 여러 메일을 눌렀으면, 주문 시각 바로 앞의 메일에 한 번만 붙입니다.',
  ];
  data.lastClick.forEach((row) => {
    lines.push(promoMailLabel(row.name));
    lines.push(`주문 ${formatNum(row.orders)}건 · ${won(row.amount)} · 기관 ${formatNum(row.buyers)}곳`);
  });
  lines.push('');
  lines.push(
    `클릭 없이 구매 주문 ${formatNum(data.boughtWithoutClick.orders)}건 · ${won(data.boughtWithoutClick.amount)} · 기관 ${formatNum(data.boughtWithoutClick.buyers)}곳`
  );
  return lines.join('\n');
};
